import { useContext } from "react";
import { Box, FormControlLabel, Switch } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Promisable } from "type-fest";

import type { TrustUnbranded } from "@thalamos/aspire-common";

import { ManagedLocationsContext } from "../../Contexts.js";
import { Typeahead } from "../design-system/Typeahead/Typeahead.js";

export type SelectedTrust = {
  /** Trust ID - only present when a trust is matched from the list */
  id?: string;
  /** The displayed/typed name - always present */
  name: string;
  odsCode?: string;
  isNotListed: boolean;
} | null;

type TrustPickerBaseProps = {
  /** Whether to show the "Trust not listed" toggle. Defaults to false */
  allowNotListed?: boolean;
  /** Whether the picker is disabled */
  disabled?: boolean;
  /** Label for the typeahead field */
  label?: string;
  /** Error message to display */
  errorMessage?: string;
  /** Label for the "not listed" toggle */
  notListedLabel?: string;
  /** Test ID for the component */
  testId?: string;
};

export type TrustPickerProps = TrustPickerBaseProps & {
  /** The currently selected trust, or null if none selected */
  value: SelectedTrust;
  /** Callback when a trust is selected or cleared */
  onChange: (trust: SelectedTrust) => Promisable<void>;
};

export function TrustPicker(props: TrustPickerProps) {
  return <TrustPickerSingle {...props} />;
}

function TrustPickerSingle({
  value,
  onChange,
  allowNotListed = false,
  disabled = false,
  label,
  errorMessage,
  notListedLabel,
  testId = "trustPicker",
}: TrustPickerProps) {
  const { t } = useTranslation();
  const managedLocationsContext = useContext(ManagedLocationsContext);

  const notListedTrust = managedLocationsContext.trusts.find(
    (trust) => trust.notListed,
  );

  const isNotListed = value?.isNotListed ?? false;

  const trustOptions = managedLocationsContext.trusts
    .filter((trust) => !trust.notListed)
    .map((trust) => ({
      label: trust.name,
      value: trust.id,
    }));

  const handleTrustChange = (val: string | null) => {
    if (isNotListed) return;

    if (!val) {
      void onChange(null);
      return;
    }

    const trust = managedLocationsContext.trusts.find((t) => t.name === val);

    if (trust) {
      void onChange({
        id: trust.id,
        name: trust.name,
        odsCode: trust.odsCode ?? undefined,
        isNotListed: false,
      });
    } else {
      // Partial/free text entry - store the typed value but no id/odsCode
      void onChange({
        id: undefined,
        name: val,
        odsCode: undefined,
        isNotListed: false,
      });
    }
  };

  const handleNotListedToggle = (checked: boolean) => {
    if (checked && notListedTrust) {
      void onChange({
        id: notListedTrust.id,
        name: notListedTrust.name,
        odsCode: notListedTrust.odsCode ?? undefined,
        isNotListed: true,
      });
    } else {
      void onChange(null);
    }
  };

  const inputValue = isNotListed
    ? (notListedTrust?.name ?? "")
    : (value?.name ?? "");

  return (
    <Box data-testid={testId}>
      <Typeahead
        label={label ?? t("forms.hospitalPicker.trust")}
        errorMessage={errorMessage}
        enableFuse={false}
        disabled={disabled || isNotListed}
        options={trustOptions}
        inputValue={inputValue}
        onInputChange={handleTrustChange}
        name="trustPicker"
      />

      {allowNotListed && (
        <FormControlLabel
          control={
            <Switch
              checked={isNotListed}
              disabled={disabled}
              onChange={(e) => {
                handleNotListedToggle(e.target.checked);
              }}
            />
          }
          label={notListedLabel ?? t("forms.hospitalPicker.trustNotListed")}
          sx={{ mt: 1 }}
        />
      )}
    </Box>
  );
}

/**
 * Helper to convert a TrustUnbranded to SelectedTrust
 */
export function trustToSelectedTrust(
  trust: TrustUnbranded | undefined | null,
): SelectedTrust {
  if (!trust) return null;
  return {
    id: trust.id,
    name: trust.name,
    odsCode: trust.odsCode ?? undefined,
    isNotListed: trust.notListed ?? false,
  };
}
